import { useDebounce } from "use-debounce";
import { MenuBoxOptionBase } from "./menu-box";
import { useQuery } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import { MentionLookupCacheProvider } from "./types";
import { skedEditorQueryClient } from "../sked-editor";

export function useMentionLookupService<T extends MenuBoxOptionBase>({
  rawMention,
  isSearchConditionMet,
  typeDebounceTime,
  dataFetcher,
  cache,
  dataResolver,
}: {
  rawMention: string | null;
  isSearchConditionMet?: (
    lookupText: string | null | undefined
  ) => SearchConditionResult;
  typeDebounceTime: number;
  dataFetcher: (lookupText: string) => Promise<T[]>;
  dataResolver?: (items: T[], lookupText: string | null | undefined) => T[];
  cache: MentionLookupCacheProvider<T> | false | undefined;
}): {
  data: T[];
  isLoading: boolean;
  activeMention: string | null;
  typeDebounceTime: number;
  error: Error | null;
  reload: () => void;
} {
  const [activeMention] = useDebounce(rawMention, typeDebounceTime);
  const [uniqueComponentId] = useState(() =>
    Math.random().toString(16).slice(2)
  );

  const [latestAvailableData, setLatestAvailableData] = useState<T[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const latestIsPendingRef = useRef(false);
  const defaultCache = useRef<MentionLookupCacheProvider<T>>(
    new MemoryCacheProvider()
  );
  function getCache() {
    if (cache === false) {
      return neverCacheProvider;
    }
    if (cache) {
      return cache;
    }
    return defaultCache.current;
  }
  const mentionsCache = getCache();

  const { data, isPending, status, error } = useQuery({
    queryKey: ["mention-results", uniqueComponentId, activeMention],
    retry: false,
    queryFn: async () => {
      // eslint-disable-next-line unicorn/consistent-function-scoping
      async function run() {
        if (
          isSearchConditionMet &&
          isSearchConditionMet?.(activeMention).status !== "success"
        ) {
          return [];
        }

        if (activeMention === null || activeMention === undefined) {
          return [];
        }

        const cachedResults = mentionsCache.get(activeMention);

        if (cachedResults !== undefined && cachedResults !== null) {
          return cachedResults;
        }

        const rs = await dataFetcher(activeMention);

        mentionsCache.set(activeMention, rs);
        return rs;
      }
      const rs = await run();
      if (dataResolver) {
        return dataResolver(rs, activeMention);
      }
      return rs;
    },
    gcTime: 0,
  });
  latestIsPendingRef.current = isPending;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (
      isSearchConditionMet &&
      isSearchConditionMet(rawMention).status === "success"
    ) {
      setIsProcessing(true);
      timeoutId = setTimeout(() => {
        if (!latestIsPendingRef.current) {
          setIsProcessing(false);
        }
      }, typeDebounceTime + 100);
    } else {
      setIsProcessing(false);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMention]);

  useEffect(() => {
    setIsProcessing(isPending);
  }, [isPending, data]);

  useEffect(() => {
    if (status === "success") {
      setLatestAvailableData(data || []);
    }
  }, [data, status, activeMention]);

  return {
    data: latestAvailableData,
    isLoading: isProcessing,
    activeMention,
    typeDebounceTime,
    error,
    reload: () => {
      mentionsCache.clear();
      skedEditorQueryClient.invalidateQueries();
    },
  };
}

export interface SearchConditionResult {
  status: "success" | "error";
  conditionMessage?: string;
}

export class MemoryCacheProvider<T extends MenuBoxOptionBase>
  implements MentionLookupCacheProvider<T>
{
  private map = new Map<string, T[]>();
  get(searchValue: string | null | undefined): T[] | null | undefined {
    if (searchValue === undefined || searchValue === null) {
      return null;
    }
    return this.map.get(searchValue);
  }
  set(searchValue: string, result: T[]): void {
    this.map.set(searchValue, result);
  }
  clear(): void {
    this.map.clear();
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const neverCacheProvider: MentionLookupCacheProvider<any> = {
  get: () => {
    // eslint-disable-next-line unicorn/no-useless-undefined
    return undefined;
  },
  set: () => {},
  clear: () => {},
};
